<template>
    <div class="setting-page-content-wrapper features">
        <div class="page-header">
            <div class="title">{{ $t("settings.features") }}</div>
            <div class="subtitle">{{ $t("settings.featuresSubtitles") }}</div>
        </div>
        <section>
            <div class="features">
                <FeatureItem
                    :title="feature.title"
                    :description="feature.description"
                    v-for="feature in features"
                    :value="readValue(feature.id)"
                    @input="setValue(feature.id, $event)"
                    :key="feature.id"
                    :icon="feature.icon"
                />
            </div>
        </section>
    </div>
</template>

<script>
import { mapState } from "vuex";
import log2 from "../../../../utils/log2";
import WirelessKeyboardIcon from "../../../../components/svg/icons/WirelessKeyboardIcon.vue";
import FeatureItem from "../components/FeatureItem.vue";
import MergeIcon from "../../../../components/svg/icons/MergeIcon.vue";

const log = log2("Settings/Features");

export default {
    name: "Features",
    components: {
        WirelessKeyboardIcon,
        FeatureItem,
    },
    props: {
        updateSettings: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            features: [
                {
                    title: this.$t("message.customerTyping"),
                    description: this.$t("message.vtipyCustomerTyping"),
                    icon: "WirelessKeyboardIcon",
                    id: "writing",
                },
                {
                    title: this.$t("message.agentTyping"),
                    description: this.$t("message.vtipyAgentTyping"),
                    icon: "writingIcon",
                    id: "agentTyping",
                },
                {
                    title: this.$t("message.selectTopic"),
                    description: this.$t("message.vtipySelectTopic"),
                    icon: "DepartmentIcon",
                    id: "subject",
                },
                {
                    title: this.$t("message.queueNumberVisibleFeature"),
                    description: this.$t("message.vtipyQueueNumberVisibleFeature"),
                    icon: "QueueIcon",
                    id: "queueNumberVisible",
                },
                {
                    title: this.$t("message.bannedWordRestrict"),
                    description: this.$t("message.bannedWordRestrictDesc"),
                    icon: "BannedWordIcon",
                    id: "bannedWordMarking",
                },
                {
                    title: this.$t("message.ticketShow"),
                    description: this.$t("message.ticketShowDesc"),
                    icon: "TicketShow",
                    id: "ticketShow",
                },
                {
                    title: this.$t("message.audioCallEnabled"),
                    description: this.$t("message.audioCallDesc"),
                    icon: "voiceCall",
                    id: "audiocallEnabled",
                },
                {
                    title: this.$t("message.videoCallEnabled"),
                    description: this.$t("message.videoCallEnabledDesc"),
                    icon: "videoCallIcon",
                    id: "videocallEnabled",
                },
                {
                    title: this.$t("message.agentVoiceRecordEnabled"),
                    description: this.$t("message.agentVoiceRecordEnabledDesc"),
                    icon: "soundRecordingIcon",
                    id: "agentVoiceRecordEnabled",
                },
                {
                    title: this.$t("message.visitorVoiceRecordEnabled"),
                    description: this.$t("message.visitorVoiceRecordEnabledDesc"),
                    icon: "microphoneIcon",
                    id: "visitorVoiceRecordEnabled",
                },
                {
                    title: this.$t("message.autoCallTransfer"),
                    description: this.$t("message.vtipyAutoCallTransfer"),
                    icon: "automaticCallTransfer",
                    id: "autoTake",
                },
                {
                    title: this.$t("message.privateChatEnabled"),
                    description: this.$t("message.vtipyPrivateChatEnabled"),
                    icon: "speechIcon",
                    id: "privateChatEnabled",
                },
                {
                    title: this.$t("message.agentFileSendEnabled"),
                    description: this.$t("message.agentFileSendEnabledDesc"),
                    icon: "representativeFile",
                    id: "agentFileSendEnabled",
                },
                {
                    title: this.$t("message.visitorFileSendEnabled"),
                    description: this.$t("message.visitorFileSendEnabledDesc"),
                    icon: "fileIcon",
                    id: "visitorFileSendEnabled",
                },
                {
                    title: this.$t("message.visitorWriteEmptyChat"),
                    description: this.$t("message.visitorWriteEmptyChatTippy"),
                    icon: "customerRepresentativeIcon",
                    id: "visitorWriteEmptyChat",
                },
                {
                    title: this.$t("message.disableAgentSystemMessages"),
                    description: this.$t("message.disableAgentSystemMessagesDesc"),
                    icon: "secretMessageIcon",
                    id: "disableAgentSystemMessages",
                },
                {
                    title: this.$t("message.visitorGDPRDeleteEnabled"),
                    description: this.$t("message.visitorGDPRDeleteEnabledDesc"),
                    icon: "visitorClearIcon",
                    id: "visitorGDPRDeleteEnabled",
                },
                {
                    title: this.$t("message.visitorConnectionEventSystemMessageEnabled"),
                    description: this.$t("message.visitorConnectionEventSystemMessageEnabledTippy"),
                    icon: "messageIcon",
                    id: "visitorConnectionEventSystemMessageEnabled",
                },
                {
                    title: this.$t("message.autoTranslate"),
                    description: this.$t("message.disableAutoTranslateDesc"),
                    icon: "translationIcon",
                    id: "autoTranslate",
                },
                {
                    title: this.$t("message.visitorAutoLogin"),
                    description: this.$t("message.visitorAutoLoginDesc"),
                    icon: "automaticLoginIcon",
                    id: "visitorAutoLogin",
                },
                {
                    title: this.$t("message.visitorLocationEnabled"),
                    description: this.$t("message.visitorLocationEnabled"),
                    icon: "visitorLocationIcon",
                    id: "visitorLocationEnabled",
                },
                {
                    title: this.$t("message.customerLocationEnabled"),
                    description: this.$t("message.customerLocationEnabled"),
                    icon: "locationIcon",
                    id: "customerLocationEnabled",
                },
                {
                    title: this.$t("message.customerAutoMergeEnabled"),
                    description: this.$t("message.customerAutoMergeEnabledDesc"),
                    icon: "MergeIcon",
                    id: "visitorAutoMergeEnabled",
                },
                {
                    title: this.$t("message.chatAiCopilot"),
                    description: this.$t("message.chatAiCopilotDesc"),
                    icon: "ChatAiIcon",
                    id: "chatAiCopilot",
                },
            ],
        };
    },
    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            site: (state) => state.site.site,
        }),
    },
    methods: {
        readValue(id) {
            log("readValue", id, this.settings[id]);
            if (["customerClientLanguageDetect", "subject", "autoTake", "writing", "bannedWord"].includes(id)) {
                return this.settings[id] == true;
            } else {
                return this.settings[id];
            }
        },
        setValue(id, value) {
            this.updateSettings(id, value);
        },
    },
    mounted() {},
    watch: {},
};
</script>

<style scoped lang="scss">
.features {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
</style>
